import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { CheckCircle, Warning, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';

const StripeOnboardingReturnPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<{
    onboardingComplete: boolean;
    readyForPayouts: boolean;
    message: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkOnboardingStatus();
  }, []);

  const checkOnboardingStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.get('/stripe-connect/onboarding-return');
      if (response.success && response.data) {
        setStatus(response.data as any);
      } else {
        setError(response.error || 'Failed to check onboarding status');
      }
    } catch (err: any) {
      console.error('Error checking onboarding status:', err);
      setError(err.response?.data?.error || 'Failed to check onboarding status');
    } finally {
      setLoading(false);
    }
  };

  if (user?.userType !== 'provider') {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Alert severity="error">
            This page is only accessible to providers.
          </Alert>
        </Paper>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4 }}>
        {error ? (
          <>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button
              variant="contained"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/profile')}
              fullWidth
            >
              Return to Profile
            </Button>
          </>
        ) : status?.readyForPayouts ? (
          <>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <CheckCircle color="success" sx={{ fontSize: 60 }} />
            </Box>
            <Typography variant="h5" align="center" gutterBottom>
              Onboarding Complete!
            </Typography>
            <Alert severity="success" sx={{ my: 2 }}>
              Your Stripe Connect account is fully set up and ready to receive payouts!
            </Alert>
            <Typography variant="body2" color="text.secondary" align="center" paragraph>
              {status.message}
            </Typography>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate('/profile')}
              sx={{ mt: 2 }}
            >
              Return to Profile
            </Button>
          </>
        ) : (
          <>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <CheckCircle color="success" sx={{ fontSize: 60 }} />
            </Box>
            <Typography variant="h5" align="center" gutterBottom>
              Onboarding In Progress
            </Typography>
            <Alert severity="success" sx={{ my: 2 }}>
              {status?.message || 'Please complete all required information to finish setup.'}
            </Alert>
            <Typography variant="body2" color="text.secondary" align="center" paragraph>
              You may need to provide additional information or documentation to complete your account setup.
            </Typography>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate('/profile')}
              sx={{ mt: 2 }}
            >
              Return to Profile
            </Button>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default StripeOnboardingReturnPage;
